import React, { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { LangProvider } from "@/contexts/LangContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import CompareBar from "@/components/CompareBar";
import LogoLoader from "@/components/LogoLoader";

// Eager: Landing is the first paint the user sees; AuthCallback runs during
// render for the Google OAuth hash detour and can't wait for chunk-load.
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";

// Everything else is code-split so route transitions ship the smallest
// possible JS bundle. The Suspense fallback below renders the branded
// LogoLoader while the chunk downloads, so users see a Mazad AD emblem
// (not a blank flash of white) as they navigate.
const Catalog = lazy(() => import("@/pages/Catalog"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const ScanRef = lazy(() => import("@/pages/ScanRef"));
const SellWatch = lazy(() => import("@/pages/SellWatch"));
const AuctionsList = lazy(() => import("@/pages/AuctionsList"));
const AuctionLive = lazy(() => import("@/pages/AuctionLive"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminCustomerProfile = lazy(() => import("@/pages/AdminCustomerProfile"));
const FindMyWatch = lazy(() => import("@/pages/FindMyWatch"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const RequestWatch = lazy(() => import("@/pages/RequestWatch"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const Cart = lazy(() => import("@/pages/Cart"));
const Compare = lazy(() => import("@/pages/Compare"));
const Atelier = lazy(() => import("@/pages/Atelier"));
const MyAccount = lazy(() => import("@/pages/MyAccount"));
const PurchaseHistory = lazy(() => import("@/pages/PurchaseHistory"));
const PaymentsDue = lazy(() => import("@/pages/PaymentsDue"));
const PaymentHistory = lazy(() => import("@/pages/PaymentHistory"));
const TrackMyWatch = lazy(() => import("@/pages/TrackMyWatch"));
const SellerOnboardingComplete = lazy(() => import("@/pages/SellerOnboardingComplete"));
const EvaluateWatch = lazy(() => import("@/pages/EvaluateWatch"));
const MyEvaluations = lazy(() => import("@/pages/MyEvaluations"));
const EvaluationDetail = lazy(() => import("@/pages/EvaluationDetail"));

// Customer-facing legal / brand pages linked from the site footer.
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const ReturnPolicy = lazy(() => import("@/pages/ReturnPolicy"));
const Authentication = lazy(() => import("@/pages/Authentication"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const About = lazy(() => import("@/pages/About"));

function Router() {
  const location = useLocation();
  // Handle Google OAuth callback via hash - detect during render (not effect) to prevent race
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen bg-midnight-deep"><LogoLoader fullscreen /></div>}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/catalog/:productId" element={<ProductDetail />} />
          <Route path="/s/:ref" element={<ScanRef />} />
          <Route path="/sell" element={<SellWatch />} />
          <Route path="/request-watch" element={<RequestWatch />} />
          <Route path="/find-my-watch" element={<FindMyWatch />} />
          <Route path="/auctions" element={<AuctionsList />} />
          <Route path="/auctions/:lotId" element={<AuctionLive />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/atelier" element={<Atelier />} />
          <Route path="/services" element={<Atelier />} />
          <Route path="/beyond-the-sale" element={<Atelier />} />
          <Route path="/evaluate" element={<EvaluateWatch />} />
          <Route path="/me/evaluations" element={<MyEvaluations />} />
          <Route path="/me/evaluations/:evalId" element={<EvaluationDetail />} />
          <Route path="/account" element={<MyAccount />} />
          <Route path="/account/history" element={<PurchaseHistory />} />
          <Route path="/account/payments" element={<PaymentHistory />} />
          <Route path="/account/payments-due" element={<PaymentsDue />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/customers/:userId" element={<AdminCustomerProfile />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/track/:channel/:sourceId" element={<TrackMyWatch />} />
          <Route path="/seller/onboarding/complete" element={<SellerOnboardingComplete />} />
          <Route path="/seller/onboarding/refresh" element={<SellerOnboardingComplete />} />
          {/* Customer-facing legal / brand pages linked from the footer. */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/trust" element={<Authentication />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
        </Routes>
      </Suspense>
    </>
  );
}

// Resets scroll to top on every route change — but leaves in-page hash targets
// (e.g. /#services) to the target page's own scroll handler.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let hash-scroll handlers do their thing
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <CurrencyProvider>
              <CartProvider>
                <FavoritesProvider>
                  <CompareProvider>
                    <Router />
                    <CompareBar />
                    <Toaster theme="dark" position="bottom-right" />
                  </CompareProvider>
                </FavoritesProvider>
              </CartProvider>
            </CurrencyProvider>
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
